import React, { useRef } from 'react'
import classNames from 'classnames'
import { useDrag, useDrop } from 'react-dnd'
import ExperienceDeleteIcon from './ExperienceDeleteIcon'

/**
 * Watch / Read attached row — drag starts only from the ⋮⋮ handle.
 * Hover tracks the target index only; parent splices + saves on drop.
 */
const LearningAttachedDndRow = ({
  dndType,
  id,
  index,
  title,
  subtitle,
  typeLabel,
  isEditing = false,
  disabled = false,
  onDragEnd,
  onOpen,
  onRequestRemove,
}) => {
  const rowRef = useRef(null)
  const handleRef = useRef(null)

  const [{ handlerId, isOver }, drop] = useDrop({
    accept: dndType,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
        isOver: monitor.isOver({ shallow: true }),
      }
    },
    hover(item) {
      if (disabled || !rowRef.current) return
      if (item.targetIndex === index) return
      item.targetIndex = index
    },
    drop(item) {
      return { id: item.id, targetIndex: item.targetIndex }
    },
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: dndType,
    item: () => ({ id, index, targetIndex: index }),
    canDrag: !disabled,
    end(item, monitor) {
      if (disabled || !item || typeof onDragEnd !== 'function') return
      if (!monitor.didDrop()) return
      const toIndex =
        typeof item.targetIndex === 'number' ? item.targetIndex : item.index
      if (toIndex === item.index) return
      onDragEnd({
        id: item.id,
        fromIndex: item.index,
        toIndex,
      })
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })

  preview(drop(rowRef))
  drag(handleRef)

  const handleOpen = () => {
    if (disabled) return
    if (typeof onOpen === 'function') {
      onOpen()
    }
  }

  return (
    <div
      ref={rowRef}
      data-handler-id={handlerId}
      className={classNames('experience-aud-row', {
        'is-editing': isEditing,
        'aud-dragging': isDragging,
        'is-drag-over': isOver && !isDragging,
      })}
      style={{ opacity: isDragging ? 0.35 : 1 }}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen && !disabled ? 0 : undefined}
      onClick={handleOpen}
      onKeyDown={(e) => {
        if (!onOpen || disabled) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleOpen()
        }
      }}>
      <span
        ref={handleRef}
        className='experience-grab'
        title={disabled ? undefined : 'Drag to reorder'}
        aria-disabled={disabled || undefined}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}>
        ⋮⋮
      </span>
      <span className='experience-qnum'>{index + 1}</span>
      <span className='experience-aud-thumb' aria-hidden='true' />
      <span className='copy'>
        <b>{title || 'Untitled'}</b>
        {subtitle ? <small>{subtitle}</small> : null}
      </span>
      {typeLabel ? <span className='experience-atype'>{typeLabel}</span> : null}
      {typeof onRequestRemove === 'function' ? (
        <button
          type='button'
          className='experience-x'
          aria-label='Remove'
          title='Remove'
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation()
            if (disabled) return
            onRequestRemove()
          }}>
          <ExperienceDeleteIcon />
        </button>
      ) : null}
    </div>
  )
}

export default LearningAttachedDndRow
